import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import JobsApiClient from '@/lib/api';

export default function LoginPage() {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuthApiKey = useAuthStore((state) => state.setApiKey);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Test the API key
      const client = new JobsApiClient({
        baseUrl: '',
        apiKey: apiKey.trim(),
      });

      const isValid = await client.testConnection();

      if (isValid) {
        setAuthApiKey(apiKey.trim());
        navigate('/');
      } else {
        setError('Invalid API key');
      }
    } catch (err) {
      setError('Failed to connect to API. Please check your API key.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md px-4">
        <div className="glass-card rounded-lg p-8 space-y-6">
          {/* Logo */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-primary to-chart-2 mb-4">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-10 h-10"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="currentColor" className="text-white" />
                    <stop offset="100%" stopColor="currentColor" className="text-white/80" />
                  </linearGradient>
                </defs>
                <rect x="3" y="5" width="18" height="1.5" rx="0.75" fill="url(#logo-gradient)" opacity="0.5" />
                <rect x="3" y="11" width="18" height="1.5" rx="0.75" fill="url(#logo-gradient)" />
                <rect x="3" y="17" width="18" height="1.5" rx="0.75" fill="url(#logo-gradient)" opacity="0.5" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-chart-2 bg-clip-text text-transparent">
              BetterInfra Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">
              Monitor your Jobs API
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="apiKey" className="text-sm font-medium">
                API Key
              </label>
              <input
                id="apiKey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your API key"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={loading}
                required
              />
            </div>

            {error && (
              <div className="text-sm text-destructive p-3 bg-destructive/10 rounded-md border border-destructive/20">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !apiKey.trim()}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Connecting...' : 'Connect to API'}
            </button>
          </form>

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground">
            Powered by BG-Brain Jobs API
          </div>
        </div>
      </div>
    </div>
  );
}
